import "./Billing.scss";

import {
  Alert,
  Button,
  ExclamationTriangleIcon,
  Flex,
  FlexItem,
  Text,
} from "@fluentui/react-northstar";
import { PaymentFrequency } from "@teams/types";
import { StripeBillingInfoDTO, StripePaymentDTO, UserSubscriptionDTO } from "@teams/types/api";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useUserInfo } from "../../actions/useUserInfo";
import CoreConfirmationAnimation from "../../assets/new-external-tab-image.svg";
import { PollyDialog } from "../../components/PollyDialog";
import { PollyIcon } from "../../components/PollyIcon";
import { TeamsLoader } from "../../components/TeamsLoader";
import { isError } from "../../services/ChannelApi";
import { paymentsApi } from "../../services/PaymentsApi";
import { useTeamsContext } from "../../state/TeamsContextProvider";
import { getPlanLabel } from "../../types/subscriptionHelper";
import { isMobileClient } from "../../utils/clientHelper";
import { clientEventTracker } from "../../utils/clientTelemetry";
import { formatCurrency } from "../../utils/currency";
import { getPlanLevelDisplayText } from "../../utils/paymentHelpers";
import { getPageContext } from "../../utils/teamsHelpers";
import { BoltAlert } from "./BoltAlert";
import { CancelSubscription, CancelSubscriptionConfirmation } from "./CancelSubscription";

type Props = {
  subscription: UserSubscriptionDTO;
  onUpgradeRequest: () => void;
};

export const Billing: React.FC<Props> = ({ subscription, onUpgradeRequest }) => {
  const { teamsContext } = useTeamsContext();
  const { loadUser, user } = useUserInfo();

  const [stripeBillingInfo, setStripeBillingInfo] = useState<StripeBillingInfoDTO>();
  const [subscriptionError, setSubscriptionError] = useState<Error>();
  const [isSubscriptionLoading, setSubscriptionLoading] = useState<boolean>(false);
  const [isUpdateWindowOpen, setIsUpdateWindowOpen] = useState<boolean>(false);
  const [isCancellationOpen, setIsCancellationOpen] = useState<boolean>(false);
  const [isCancellationConfirmationOpen, setIsCancellationConfirmationOpen] =
    useState<boolean>(false);
  const [isReactivating, setIsReactivating] = useState<boolean>();
  const isAgile = useMemo(() => getPageContext() === "/agile", []);

  const loadSubscription = useCallback(
    async (subscriptionId: string) => {
      setSubscriptionError(undefined);
      setSubscriptionLoading(true);

      const result = await paymentsApi.getStripeSubscriptionBillingInfo(
        teamsContext,
        subscriptionId
      );

      if (isError(result)) {
        setSubscriptionError(result);
      } else {
        setStripeBillingInfo(result);
      }
      setSubscriptionLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (subscription.provider !== "stripe") {
      return;
    }

    loadSubscription(subscription.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription.updatedAt]);

  const paymentUpdateLink = useMemo(
    () => `${window.location.origin}${getPageContext()}/content/stripe/payments/update`,
    []
  );

  const launchBillingUpdate = () => {
    setIsUpdateWindowOpen(true);
    window.open(paymentUpdateLink, "_blank");
  };

  const getCardIconUrl = (brand: string | undefined) => {
    let name = "Visa.png";
    switch (brand) {
      case "mastercard":
        name = "MasterCard.png";
        break;
      case "amex":
        name = "AmericanExpress.png";
        break;
      case "discover":
        name = "Discover.png";
        break;
      case "diners":
        name = "DinersClub.png";
        break;
      case "jcb":
        name = "JCB.png";
        break;
    }
    return `https://static.polly.ai/img/cards/${name}`;
  };

  const getNumFormat = (payment: StripePaymentDTO) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: payment.currency,
    });
  };

  const getBillingFrequencyText = (frequency?: PaymentFrequency) => {
    switch (frequency) {
      case "monthly":
        return "Monthly";
      case "quarterly":
        return "Quarterly";
      case "semi_annually":
        return "Semi-Annual";
      case "yearly":
        return "Annual";
      default:
        return "";
    }
  };

  return (
    <TeamsLoader isLoading={isSubscriptionLoading} loadingMessage="Loading billing information...">
      <Flex column gap="gap.large">
        {subscriptionError && (
          <Alert content={subscriptionError.message} danger variables={{ error: true }} />
        )}

        <Flex column gap="gap.medium">
          {!!stripeBillingInfo?.cancelAtPeriodEnd && (
            <Alert
              warning
              icon={<ExclamationTriangleIcon />}
              content={`Your Polly ${getPlanLabel(
                subscription.plan,
                subscription.planLevel,
                subscription.scope
              )} ${
                stripeBillingInfo?.isTrial ? "Trial" : ""
              } subscription has been cancelled. It will not renew after ${moment(
                stripeBillingInfo.currentPeriodEnd
              ).format("LL")}.`}
              actions={[
                {
                  content: "Reactivate Subscription",
                  key: "reactivate",
                  primary: true,
                  disabled: isReactivating,
                  loading: isReactivating,
                  onClick: async () => {
                    setIsReactivating(true);
                    const result = await paymentsApi.reactivateStripeSub(
                      teamsContext,
                      subscription.id
                    );
                    if (!isError(result)) {
                      await loadUser(true);
                      await loadSubscription(subscription.id);
                    }
                  },
                },
              ]}
            />
          )}
          <Flex
            gap="gap.medium"
            className="plan-and-billing-hero"
            column={isMobileClient(teamsContext)}
          >
            <Flex className={`hero-column ${isMobileClient(teamsContext) ? "mobile" : ""} `} column>
              <Flex
                column
                className={`plan-details-box with-border ${
                  isMobileClient(teamsContext) || subscription.scope === "tenant" || isAgile
                    ? "full-height"
                    : ""
                }`}
              >
                {/* Polly Plan Information Box */}
                <Flex>
                  <Flex gap="gap.smaller" className="info-header">
                    {isAgile
                      ? "AgilePolly for Standups"
                      : `Polly ${getPlanLabel(
                          subscription.plan,
                          subscription.planLevel,
                          subscription.scope
                        )} ${stripeBillingInfo?.isTrial ? " Trial" : " Plan"}`}

                    {!stripeBillingInfo?.cancelAtPeriodEnd && (
                      <Button
                        data-tname={`cancel-${subscription.type}-subscription"}`}
                        onClick={e => {
                          e.preventDefault();
                          setIsCancellationConfirmationOpen(true);
                          clientEventTracker.track(
                            stripeBillingInfo?.isTrial
                              ? "CancelTrial button clicked"
                              : "CancelSubscription button clicked",
                            user,
                            {}
                          );
                        }}
                        text
                        content={
                          <Text
                            content={`Cancel ${
                              stripeBillingInfo?.isTrial ? "Trial" : "Subscription"
                            }`}
                            color="red"
                            weight="regular"
                          />
                        }
                        className="cancel-button"
                      />
                    )}
                  </Flex>
                </Flex>
                <Flex gap="gap.medium">
                  {stripeBillingInfo?.planLevel && (
                    <FlexItem grow>
                      <Flex column className="details-item-column">
                        <Text
                          content="Monthly Responses"
                          className="accountsettings-billing__label"
                        />
                        <Text content={getPlanLevelDisplayText(stripeBillingInfo.planLevel)} />
                      </Flex>
                    </FlexItem>
                  )}

                  {stripeBillingInfo &&
                    !stripeBillingInfo.cancelAtPeriodEnd &&
                    subscription.planUsageType === "licensed" &&
                    subscription.provider === "stripe" && (
                      <FlexItem grow>
                        <Flex column className="details-item-column">
                          <Text content="Licenses" className="accountsettings-billing__label" />
                          <Text content={stripeBillingInfo?.quantity} />
                        </Flex>
                      </FlexItem>
                    )}

                  <FlexItem grow>
                    <Flex column className="details-item-column">
                      <Text
                        content="Billing Frequency"
                        className="accountsettings-billing__label"
                      />
                      <Text
                        content={getBillingFrequencyText(stripeBillingInfo?.paymentFrequency)}
                      />
                    </Flex>
                  </FlexItem>
                </Flex>
              </Flex>
              {!isAgile && !isMobileClient(teamsContext) && subscription.scope !== "tenant" && (
                <BoltAlert
                  onButtonClick={onUpgradeRequest}
                  buttonCta="Change Plan"
                  prompt="Need more licenses or to change plan levels?"
                  showButton
                />
              )}
            </Flex>
            {/* Payment information box */}
            <Flex
              column
              gap="gap.medium"
              className={`plan-details-box hero-column ${
                isMobileClient(teamsContext) ? "mobile" : ""
              } `}
            >
              <Text weight="bold" content="Payment Information" className="info-header" />
              <Flex gap="gap.medium">
                {stripeBillingInfo?.payments.next && (
                  <FlexItem grow>
                    <Flex column className="details-item-column">
                      <Text content="Next Payment" className="accountsettings-billing__label" />
                      <Text
                        content={`${formatCurrency(stripeBillingInfo.payments.next.total / 100)}`}
                      />
                      <Text
                        content={` on ${moment(stripeBillingInfo.payments.next.date).format("LL")}`}
                      />
                    </Flex>
                  </FlexItem>
                )}

                {(stripeBillingInfo?.customerName || stripeBillingInfo?.customerEmail) && (
                  <>
                    {stripeBillingInfo?.customerName && (
                      <FlexItem grow>
                        <Flex column className="details-item-column">
                          <Text content="Billing User" className="accountsettings-billing__label" />
                          <Text>{stripeBillingInfo.customerName}</Text>
                        </Flex>
                      </FlexItem>
                    )}

                    {stripeBillingInfo?.customerEmail && (
                      <FlexItem grow>
                        <Flex column className="details-item-column">
                          <Text
                            content="Billing User Email"
                            className="accountsettings-billing__label"
                          />
                          <Text>{stripeBillingInfo.customerEmail}</Text>
                        </Flex>
                      </FlexItem>
                    )}
                  </>
                )}
              </Flex>

              {stripeBillingInfo?.payment && (
                <div className="accountsettings-billing__credit-card-info">
                  <img
                    className="accountsettings-billing__card-icon"
                    src={getCardIconUrl(stripeBillingInfo.payment.cardBrand)}
                    alt="Card Logo"
                  />
                  <span className="accountsettings-billing__card-number">
                    **** **** **** {stripeBillingInfo.payment.cardLast4Digits ?? "****"}
                  </span>
                  <Button
                    data-tname="change-billing-option"
                    primary
                    text
                    onClick={e => {
                      e.preventDefault();
                      launchBillingUpdate();
                    }}
                    color="brand"
                  >
                    Change
                  </Button>
                </div>
              )}
            </Flex>
          </Flex>
        </Flex>

        <Flex column gap="gap.medium">
          <Flex column gap="gap.small">
            <Text size="large" content="Billing History" className="info-header" />
            <Text content="Your billing history related to this account" />
          </Flex>

          <div className="agile-billing-history-table">
            <Flex className="agile-billing-history-table__table" column>
              {stripeBillingInfo?.billingHistory.map((invoice, index) => {
                const numFormat = getNumFormat(invoice);
                return (
                  <Flex
                    key={invoice.invoiceNumber}
                    className="table-row"
                    style={{
                      marginTop: index === 0 ? "0px" : "4px",
                    }}
                  >
                    <Flex gap="gap.large" className="invoice-detail-data">
                      <Text content={invoice.invoiceNumber} className="invoice-detail-item" />

                      <Text
                        content={moment(invoice.date).format("MMM D, YYYY")}
                        className="invoice-detail-item"
                      />

                      <Text
                        content={numFormat.format(Number(invoice.total) / 100)}
                        className="invoice-detail-item"
                      />
                    </Flex>
                    <Flex className="invoice-download-icons">
                      <a
                        href={invoice.invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="agile-billing-history-table__link"
                      >
                        <PollyIcon inheritColor icon="view-icon" />
                      </a>

                      <a
                        href={invoice.invoicePdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="agile-billing-history-table__link"
                      >
                        <PollyIcon inheritColor icon="download-icon" />
                      </a>
                    </Flex>
                  </Flex>
                );
              })}
            </Flex>
          </div>
        </Flex>
      </Flex>

      {isCancellationConfirmationOpen && stripeBillingInfo && (
        <CancelSubscriptionConfirmation
          onClose={() => {
            setIsCancellationConfirmationOpen(false);
          }}
          onConfirm={() => {
            setIsCancellationConfirmationOpen(false);
            setIsCancellationOpen(true);
          }}
          isTrial={stripeBillingInfo?.isTrial}
        />
      )}

      {isCancellationOpen && stripeBillingInfo && (
        <CancelSubscription
          subscription={subscription}
          stripeBillingInfo={stripeBillingInfo}
          onClose={reload => {
            loadUser(true);
            setIsCancellationOpen(false);
            if (reload) {
              loadSubscription(subscription.id);
            }
          }}
        />
      )}

      {isUpdateWindowOpen && (
        <PollyDialog
          closeOnOutsideClick={false}
          maxWidth="600px"
          open
          onCancel={() => {
            setIsUpdateWindowOpen(false);
            loadSubscription(subscription.id);
            loadUser(true);
          }}
        >
          <Flex column={true} gap="gap.small">
            <Text
              content="Update page opened in your browser"
              size="larger"
              weight="bold"
              align="center"
              as="div"
            />
            <img
              src={CoreConfirmationAnimation}
              className="congratulations__success_img"
              alt="Success"
              height={150}
            />

            <Text>
              A page to submit your payment details should have opened in a browser window. If you
              do not see the payment page then please{" "}
              <a
                href={paymentUpdateLink}
                className="brand-link underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                click here
              </a>
              . Once you have completed updating your payment details close this window to continue.
            </Text>
          </Flex>
        </PollyDialog>
      )}
    </TeamsLoader>
  );
};
